'use client';

import React from 'react';
import { ReactNode, useEffect, useState } from 'react';
import {
  PlayerPortalsChampionship,
  PlayerPortalsConfigContext,
  PlayerPortalsSiteSettings,
} from './PlayerPortalsConfigContext';
import { ppTrpc } from '@r-and-a-shared-ui/randa-trpc-client/react';
import {
  getChampKontentCollectionBasedOnEnv,
  hasValue,
  PPDefaultChampionship,
} from '@r-and-a-shared-ui/utils';
import { getGlobalDataMapping } from '@r-and-a-shared-ui/core-data-mapping';
import { RenderOn } from '../../lib/RenderOn/RenderOn';

export interface PlayerPortalsConfigProviderProps {
  children?: ReactNode;
  initialSiteSettings?: PlayerPortalsSiteSettings;
}

export const PlayerPortalsConfigProvider: React.FC<
  PlayerPortalsConfigProviderProps
> = ({ children, initialSiteSettings }) => {
  const [siteSettings, setSiteSettings] = useState<
    PlayerPortalsSiteSettings | undefined
  >(initialSiteSettings);

  const [currentChampionship, setCurrentChampionship] = useState<
    PlayerPortalsChampionship | undefined
  >(undefined);

  const { data, isFetching } = ppTrpc.playerPortalsConfig.portal.useQuery(
    undefined,
    {
      enabled: !hasValue(initialSiteSettings),
      select: config =>
        getGlobalDataMapping().KONTENT.player_portals_configuration(
          config,
        ) as PlayerPortalsSiteSettings,
    },
  );

  useEffect(() => {
    if (hasValue(data) && !isFetching) {
      setSiteSettings(data);
    }
  }, [data, isFetching]);

  useEffect(() => {
    const champCollection = getChampKontentCollectionBasedOnEnv();

    const foundChampionship =
      siteSettings?.playerPortals?.availableChampionships?.find(
        champ => champ.kontentCollection === champCollection,
      );

    const defaultChampionship =
      siteSettings?.playerPortals?.availableChampionships?.find(
        champ => champ.kontentCollection === PPDefaultChampionship,
      );

    setCurrentChampionship(
      foundChampionship ??
        defaultChampionship ??
        siteSettings?.playerPortals?.availableChampionships?.[0],
    );
  }, [currentChampionship, siteSettings]);

  return (
    <PlayerPortalsConfigContext.Provider
      value={{
        siteSettings,
        currentChampionship,
        methods: { setCurrentChampionship },
      }}
    >
      <RenderOn condition={hasValue(siteSettings)}>{children}</RenderOn>
    </PlayerPortalsConfigContext.Provider>
  );
};
